import React, { useState } from "react";
import { Box, Button, Collapse } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LOCALIZATION } from "../../utils/localization";
import "./BlogContent.scss";

const LiveDarshanContent: React.FC<any> = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <Box>
      <h1>Live Darshan – Witness the Divine Anytime, Anywhere</h1>

      <Collapse in={expanded} collapsedSize={100}>
        <p>
          Not every devotee can travel to every sacred shrine, but faith finds its way.
          With Temple Dekho’s Live Darshan, you can now experience recorded darshan videos 
          from <a href="/temples" className="linkedText">India’s most revered temples</a>, anytime, from anywhere.
        </p>

        <p>
          These carefully captured darshan moments let you witness the divine aura of temples,
          hear the rhythmic chants, and feel the serenity that flows through each sacred space,
          all from the comfort of your home.
        </p>

        <h2>What Is Temple Dekho’s Live Darshan?</h2>
        <p>
          Our Live Darshan section offers <strong>high-quality recorded videos</strong> of temple rituals, aartis, 
          and divine moments, so that every devotee can feel spiritually connected, even when 
          distance, time, or travel challenges stand in the way.
        </p>

        <p>
          Each video preserves the purity and energy of the temple, letting you relive those 
          sacred experiences whenever your heart desires.
        </p>

        <h2>Why Choose Temple Dekho’s Recorded Darshan?</h2>
        <ul>
          <li><strong>Authentic Temple Experience:</strong> Every video is captured directly at the temple, ensuring real rituals and true temple ambiance.</li>
          <li><strong>Accessible Anytime:</strong> Unlike live streams, you can watch your darshan whenever you wish, without worrying about timing or connectivity.</li>
          <li><strong>Peaceful & Immersive:</strong> High-quality visuals and authentic sounds bring you closer to the deity’s presence.</li>
          <li><strong>For Every Devotee:</strong> Ideal for those who cannot travel due to health, distance, or time constraints.</li>
          <li>
            <strong>Spiritually Uplifting:</strong> Replaying recorded darshans helps you meditate, chant, or begin your day with divine energy. 
            To complement your spiritual connection, you can also <a href="/pujas" className="linkedText">book puja online</a> for various temples and occasions through Temple Dekho’s easy-to-use portal.
          </li>
        </ul>

        <h2>How It Works</h2>
        <ol>
          <li><strong>Visit the Live Darshan Page –</strong> Explore recorded darshan videos from different temples across India.</li>
          <li><strong>Choose Your Temple –</strong> Select from iconic shrines like Kedarnath, Vaishno Devi, Somnath, and more.</li>
          <li><strong>Play the Video –</strong> Experience the aura of the temple, chants, bells, and devotion captured in every frame.</li>
          <li><strong>Revisit Anytime –</strong> Watch again whenever you seek peace, blessings, or a moment of spiritual reflection.</li>
        </ol>

        <h2>Temples You Can Experience</h2>

        <h3>Shree Dwarkadhish Temple, Gujarat</h3>
        <p>Immerse in the eternal devotion of Lord Krishna at Dwarka, one of India’s sacred Char Dhams.</p>

        <h3>Shree Swaminarayan Temple (Bhuj Mandir), Gujarat</h3>
        <p>Experience the spiritual grandeur of Swaminarayan Bhagwan’s temple, where peace and devotion unite.</p>

        <h3>Shri Ambabai Temple, Kolhapur, Maharashtra</h3>
        <p>Seek blessings from Goddess Mahalakshmi, the divine mother of strength and prosperity.</p>

        <h3>ISKCON Temple, Juhu, Maharashtra</h3>
        <p>Feel the rhythm of devotion through the chants of Hare Krishna Hare Rama at Mumbai’s iconic ISKCON Temple.</p>

        <h3>Shree Ram Janmabhoomi Mandir, Ayodhya, Uttar Pradesh</h3>
        <p>Witness the divine glory of Lord Ram’s birthplace, the sacred heart of Ayodhya.</p>

        <h3>Trimbakeshwar Jyotirling Temple, Maharashtra</h3>
        <p>Revere one of Lord Shiva’s twelve Jyotirlingas, where spirituality flows as endlessly as the sacred river Godavari.</p>

        <h3>Shree Siddhivinayak Temple, Maharashtra</h3>
        <p>Begin your spiritual journey with Lord Ganesha’s darshan, the remover of all obstacles.</p>

        <h3>Shree Somnath Temple, Gujarat</h3>
        <p>Feel the timeless strength of Lord Shiva at Somnath, the first Jyotirlinga and a beacon of faith for millions.</p>

        <h3>Mahakaleshwar Mandir, Madhya Pradesh</h3>
        <p>Experience the divine presence of Mahakal through recorded Bhasma Aarti and sacred rituals from Ujjain.</p>

        <h3>Kashi Vishwanath Temple, Uttar Pradesh</h3>
        <p>Witness the sacred energy of Lord Vishwanath in Varanasi, where the eternal flame meets flowing faith.</p>

        <h3>Sai Baba Temple, Shirdi, Maharashtra</h3>
        <p>Feel the compassion and calm of Shirdi Sai Baba’s darshan, a symbol of universal love and devotion.</p>

        <h3>Baba Baidyanath Dham, Jharkhand</h3>
        <p>Connect with the divine vibrations of Deoghar, one of the most powerful Jyotirlingas of Lord Shiva.</p>

        <h3>Sri Naga Sai Mandir (Sri Saibaba Temple), Tamil Nadu</h3>
        <p>Seek inner peace through the divine darshan of Sai Baba at Coimbatore’s revered Naga Sai Mandir.</p>

        <h3>Pashupatinath Temple, Bagmati Province (Nepal)</h3>
        <p>Transcend borders and connect with Lord Pashupatinath, the supreme form of Shiva worshipped for eternity.</p>

        <p>(More temples are added regularly so every devotee can stay connected with their chosen deity.)</p>

        <h2>Temple Dekho’s Promise</h2>
        <p>
          Every recorded darshan you see is authentic, reverent, and temple-approved. 
          We ensure the sanctity of every video, preserving not just visuals, but the vibration of faith itself. 
          Through Temple Dekho, devotees can also participate in online chadhava or online prasadam delivery 
          services to complete their spiritual experience.
        </p>

        <p>
          Temple Dekho bridges tradition with technology, so your devotion never fades, 
          even when you’re far from the temple gates.
        </p>

        <h2>Watch, Feel, and Connect</h2>
        <p>
          Experience peace, devotion, and divine energy through Temple Dekho’s recorded Live Darshan.
          Because when your heart seeks the Divine, distance no longer matters.
        </p>
      </Collapse>

      <Button
        variant="outlined"
        sx={{ mt: "10px" }}
        onClick={handleToggle}
        color="primary"
      >
        {expanded ? "Read Less" : t(LOCALIZATION.Read_More)}
      </Button>
    </Box>
  );
};

export default LiveDarshanContent;
